import { useRef } from "react"

import * as ScanHistoryAPI from "@/api/useScanHistory"
import { isKnownUserRole, isUserAdmin } from "@/domain/user"
import { getTimestamp } from "@/helpers/date.helpers"
import { useQueryString } from "@/hooks/useQueryString"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import * as selectors from "./useScanHistory.selector"

import type {
  FilterScanHistoryResponse,
  ScanHistoryResponse
} from "@/api/useScanHistory.types"
import type { Params } from "@/hooks/useHttp"

export const useScanHistoryFeatureFlags = () => {
  const { user } = useUser()
  const enabled = isUserAdmin(user)

  return { enabled, initialize: isKnownUserRole(user) }
}

export const useScanHistory = <T extends ScanHistoryResponse>() => {
  const { fromDate, toDate, ...params } = useQueryString([
    "orderBy",
    "ascending",
    "fromDate",
    "toDate",
    ...selectors.getFiltersKeys()
  ])

  const { enabled } = useScanHistoryFeatureFlags()

  const { isLoading, ...context } = ScanHistoryAPI.useGetScanHistory<
    T,
    ReturnType<typeof selectors.getScanHistorySelector>
  >(
    {
      fromDate: getTimestamp(fromDate),
      toDate: getTimestamp(toDate),
      ...params
    },
    { select: selectors.getScanHistorySelector, enabled }
  )

  return { isLoading: isLoading || !enabled, ...context }
}

export const useFiltersScanHistory = (params?: Params) => {
  const { t } = useTranslation()
  const select = useRef((response: FilterScanHistoryResponse) =>
    selectors.getFiltersScanHistorySelector(response, t)
  ).current
  const context = ScanHistoryAPI.useGetFiltersScanHistory(params, { select })

  return context
}
